import { StrictMode, useLayoutEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppErrorBoundary } from './AppErrorBoundary';
import Root from './Root';
import { RouteRobotsMeta } from './SeoEnhancements';
import './styles.css';
import './site-chrome.css';
import './mobile-footer-fix.css';
import './home-polish.css';
import './home-enhancements.css';
import './mobile-action-dock.css';
import './a11y-contrast.css';
import './editorial-refresh.css';
import './interaction-flow.css';

// PlantGrow owns scroll positioning between client-side routes. Leaving browser
// restoration on can reopen a newly rendered route at the previous page's
// scroll position, which is especially noticeable on long mobile pages.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

function ScrollToTop() {
  const location = useLocation();

  useLayoutEffect(() => {
    // Reset synchronously before the new route is painted so users do not see a
    // flash at the previous page's scroll position. The explicit element resets
    // cover mobile browsers that do not immediately reflect window.scrollTo().
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);

  return null;
}

// Public pages can include a small server-rendered SEO fallback so crawlers and
// no-JavaScript clients receive meaningful content immediately. React replaces
// it with the full interactive application as soon as the bundle starts.
document.getElementById('seo-server-content')?.remove();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <RouteRobotsMeta />
        <Root />
      </BrowserRouter>
    </AppErrorBoundary>
  </StrictMode>,
);
